import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  FilterFn,
} from "@tanstack/react-table";
import { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Database } from "@/types/supabase";

export type InvoiceType = Database["public"]["Tables"]["invoices"]["Row"];

interface ReportsDataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
}

const DEFAULT_SORT = { id: "code", desc: true };

export function ReportsDataTable<TData extends InvoiceType, TValue>({
  columns,
  data,
}: ReportsDataTableProps<TData, TValue>) {
  const currentMonthIndex = new Date().getMonth();
  const [selectedMonthIndex, setSelectedMonthIndex] = useState<number>(currentMonthIndex);
  const [sorting, setSorting] = useState<SortingState>([DEFAULT_SORT]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  function handleSelectMonth(value: string) {
    setSelectedMonthIndex(Number(value));
  }

  const daterFilter: FilterFn<InvoiceType> = (row, columnId, filterValue) => {
    const date = new Date(row.getValue(columnId) as string);
    return date.getMonth() === Number(filterValue);
  };

  // const [rowSelection, setRowSelection] = useState({})
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    // getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    // onColumnVisibilityChange: setColumnVisibility,
    // onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      // columnVisibility,
      // rowSelection,
    },
    filterFns: {
      dater: daterFilter,
    },
  });

  const MONTHS = [
    { label: "Sausis", value: 0 },
    { label: "Vasaris", value: 1 },
    { label: "Kovas", value: 2 },
    { label: "Balandis", value: 3 },
    { label: "Gegužė", value: 4 },
    { label: "Birželis", value: 5 },
    { label: "Liepa", value: 6 },
    { label: "Rugpjūtis", value: 7 },
    { label: "Rugsėjis", value: 8 },
    { label: "Spalis", value: 9 },
    { label: "Lapkritis", value: 10 },
    { label: "Gruodis", value: 11 },
  ];

  return (
    <div>
      <div className="flex items-center justify-between py-4">
        <Select onValueChange={handleSelectMonth} value={selectedMonthIndex.toString()}>
          <SelectTrigger>
            <SelectValue placeholder="Pasirinkite mėnesį" />
          </SelectTrigger>
          <SelectContent>
            {MONTHS.map((month) => (
              <SelectItem key={month.value} value={month.value.toString()}>
                {month.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => {
                return (
                  <TableRow key={row.id} data-state={row.getIsSelected() && "selected"}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {/* <div className="flex-1 text-sm text-muted-foreground">
        {table.getFilteredSelectedRowModel().rows.length} of{" "}
        {table.getFilteredRowModel().rows.length} row(s) selected.
      </div> */}
    </div>
  );
}
